import { Inject, Injectable } from '@angular/core';
import { DwOrmDataService, DwOrmDataServiceToken, DwSecurityUserService } from '@devwareapps/devware-cap';
import { defer, Observable, of, throwError } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AppMetaData, RecipientEntity } from '../../../meta-data/app-meta-data.service';
import { orderPhotos, RecipientStory, RecipientNews } from './recipient-repository.service';

export interface RecipientPreview {
  story: RecipientStory;
  updates: RecipientNews[];
  status: string;
  updateStatuses: { [id: number]: string };
}

export function publicationLabel(published: boolean, date: string, now = Date.now()): string {
  if (!published) { return 'Draft — not published'; }
  const timestamp = date ? Date.parse(date) : NaN;
  if (!Number.isFinite(timestamp)) { return 'Not visible — publication date required'; }
  return timestamp > now ? 'Scheduled' : 'Published';
}

/** Admin-only, uncached reads. Never changes publication or uses the public cache. */
@Injectable({ providedIn: 'root' })
export class RecipientPreviewService {
  constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService, private security: DwSecurityUserService) {}
  get allowed(): boolean {
    return this.security.hasPermission('Streaker_Challenge_CanAdmin') || this.security.hasPermission('Ent_AdminCanAccess');
  }
  load(recipientId: number): Observable<RecipientPreview> {
    return defer(() => {
      if (!this.allowed) { return throwError(new Error('Administrator access is required.')); }
      const securityContext = this.security.securityContext;
      if (!Number.isSafeInteger(recipientId) || recipientId <= 0) { return of(null); }
      const photos = AppMetaData.RecipientPhoto.CreateQueryBuilder()
        .addFields(a => [a.RecipientPhotoId, a.MediaId, a.Caption, a.AltText, a.IsDefault, a.DisplayOrder, a.CardFitWholePhoto, a.CardPositionXPercent, a.CardPositionYPercent]);
      const updates = AppMetaData.RecipientUpdate.CreateQueryBuilder()
        .addFields(a => [a.RecipientUpdateId, a.Title, a.BodyHtml, a.IsPublished, a.PublishAtDateTime])
        .addOrderByDesc(a => a.PublishAtDateTime).addOrderByDesc(a => a.RecipientUpdateId)
        .addPrefetch(r => r.RecipientUpdatePhoto, AppMetaData.RecipientUpdatePhoto.CreateQueryBuilder()
          .addFields(a => [a.RecipientUpdatePhotoId, a.MediaId, a.Caption, a.AltText, a.IsDefault, a.DisplayOrder]).query);
      const query = AppMetaData.Recipient.CreateQueryBuilder()
        .addFields(a => [a.RecipientId, a.ChallengeDefinitionId, a.RecipientName, a.Slug, a.ShortDescription,
          a.FullDescriptionHtml, a.RevealAtDateTime, a.IsPublished, a.DisplayOrder])
        .addFilterAnd(f => f.Equal(a => a.RecipientId, recipientId))
        .addPrefetch(r => r.RecipientPhoto, photos.query)
        .addPrefetch(r => r.ChallengeDefinition, AppMetaData.ChallengeDefinition.CreateQueryBuilder()
          .addFields(a => [a.ChallengeDefinitionId, a.Name, a.ChallengeStartDate, a.IsCurrent]).query)
        .addPrefetch(r => r.RecipientUpdate, updates.query);
      return this.orm.executeQuery<RecipientEntity>(query).pipe(take(1), map(rows => {
        // Permission can change while the request is in flight.
        if (!this.allowed || securityContext !== this.security.securityContext) { throw new Error('Administrator access is required.'); }
        if (rows.length !== 1) { return null; }
        const row = rows[0];
        const updateStatuses: { [id: number]: string } = {};
        return {
          story: { RecipientId: row.RecipientId, ChallengeDefinitionId: row.ChallengeDefinitionId,
            RecipientName: row.RecipientName, Slug: row.Slug, ShortDescription: row.ShortDescription,
            FullDescriptionHtml: row.FullDescriptionHtml, RevealAtDateTime: row.RevealAtDateTime,
            DisplayOrder: row.DisplayOrder, ChallengeDefinition: row.ChallengeDefinition, Photos: orderPhotos(row.RecipientPhoto) },
          updates: (row.RecipientUpdate || []).map(update => {
            updateStatuses[update.RecipientUpdateId] = publicationLabel(update.IsPublished, update.PublishAtDateTime);
            return { RecipientUpdateId: update.RecipientUpdateId, Title: update.Title, BodyHtml: update.BodyHtml,
              PublishAtDateTime: update.PublishAtDateTime, Photos: orderPhotos(update.RecipientUpdatePhoto) };
          }),
          status: publicationLabel(row.IsPublished, row.RevealAtDateTime), updateStatuses,
        };
      }));
    });
  }
}
